import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { firstStep } from "../funnel/thread";
import { useOffer } from "../funnel/useOffre";
import { stripContext, withContext } from "../routes/registry";
import { useSettings, useT } from "../settings/context";
import { Icon } from "../ui/Icon";
import {
  FIXED_PAGES,
  MANAGED_PAGES,
  isFixed,
  labelKey,
  navKey,
  pageSubtitleKey,
  pagePath,
  publicKey,
  tileKey,
} from "./registry";
import { useReaderSignedIn } from "../panel/mode";
import type { SitePage } from "./registry";
import { stepPages } from "./stepPages";
import { baseOf, isAdded, withAdded } from "./repeatable";
import { PAGES_ORDER_KEY, orderedPages } from "./order";
import { isClosed } from "./closedPages";
import { ORDER_KEY, orderedEntries } from "../funnel/order";

/**
 * LA NAVIGATION DU SITE — le menu du haut, la barre du bas, et les tuiles.
 *
 * Les trois lisent LE MÊME REGISTRE et les mêmes réglages : c'est le contrat du
 * gestionnaire « Pages du site ». Le prototype le dit dans son propre code — « le
 * menu, les onglets mobiles ET les tuiles suivent le même registre. Une page se
 * règle en UN endroit ».
 *
 * ⚠️ LE MENU EST CELUI DU BUREAU, LA BARRE DU BAS CELLE DU MOBILE, et les deux
 * portent la même liste. Le CSS bascule (`.nav` n'apparaît qu'en bureau, `.tabbar`
 * qu'en dessous) : livrer l'un sans l'autre, c'est livrer la fonction à moitié des
 * visiteurs — et sur ce produit la moitié mobile est la plus grande.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** Ce que l'organisateur a activé, et sous quel nom. */
export function useSitePages() {
  const { value } = useSettings();
  const t = useT();

  /**
   * ═══ CE QUI EST ÉCRIT, PUIS LE MOT DU PRODUIT, PUIS LE REGISTRE ═══
   *
   * ⚠️ LE DICTIONNAIRE MANQUAIT AU MILIEU, et c'est ce qui laissait « Inscription » sous
   * une navigation par ailleurs anglaise : on lisait la valeur écrite, puis on retombait
   * droit sur le mot FRANÇAIS du registre.
   *
   * ⚠️ ET LES DEUX CLÉS NE SONT PAS LA MÊME, pour une étape servie en page. Renommée dans
   * le parcours, la page suit ce nom-là (`nameKey`) : c'est une seule chose, elle n'a
   * qu'un mot. Mais son REPLI est le sien — « Mes journées », et non « Dates », qui est le
   * nom de l'étape dans le fil d'Ariane. Chercher le repli sous la clé de l'étape aurait
   * rebaptisé la tuile « Dates » en français, ce que `stepPages.ts` interdit
   * explicitement (une page servie par une étape porte SON nom à elle, « Mes
   * informations » et non « Informations »).
   */
  const say = (labelKey: string, fallback: string, fallbackKey: string) => {
    const written = value(labelKey);
    return typeof written === "string" && written.trim()
      ? written
      : t(fallbackKey, fallback);
  };

  /**
   * ⚠️ UN EXEMPLAIRE AJOUTÉ N'A PAS DE MOT À LUI. « Formulaire 2 » est fabriqué depuis
   * le nom FRANÇAIS du registre, et aucun dictionnaire ne porte
   * `FO_MEETING_V2_PAGE_FORM_2_LABEL` : le site anglais disait « Form » puis
   * « Formulaire 2 » (bug #326). Son repli est donc le mot traduit de sa famille, suivi
   * de son rang. Le mot du produit, pas le nom écrit sur la première page : celle que
   * l'organisateur a appelée « Satisfaction » ne baptise pas la deuxième « Satisfaction 2 ».
   */
  const name = (page: SitePage) => {
    const base = isAdded(page) ? baseOf(page) : undefined;
    const fallback =
      isAdded(page) && base
        ? `${t(labelKey(base), base.name)} ${page.addedInstance}`
        : page.name;
    return say(page.nameKey ?? labelKey(page), fallback, labelKey(page));
  };

  /**
   * Le sous-titre de sa tuile, et de son en-tête pour la page qui l'affiche.
   *
   * Fixe et venu du registre pour presque toutes les pages : il dit ce qu'elles
   * CONTIENNENT. Les sections portent un `subtitleKey` : leur contenu est
   * celui de l'organisateur, leur description aussi.
   */
  const subtitle = (page: SitePage) =>
    say(
      page.subtitleKey ?? pageSubtitleKey(page),
      page.subtitle,
      pageSubtitleKey(page),
    );

  /**
   * ⚠️ LE DÉFAUT N'EST PAS LE MÊME DES DEUX CÔTÉS. Une page de contenu est masquée
   * tant qu'on ne l'a pas allumée (elle n'est pas encore prête) ; l'accueil et
   * l'inscription sont affichés tant qu'on ne les a pas éteints (les éteindre par
   * défaut aurait vidé le menu de tous les événements existants).
   */
  const switchedOn = (page: SitePage, key: string) =>
    isFixed(page.key) ? value(key) !== false : value(key) === true;

  /**
   * ═══ LES ÉTAPES QUE L'ORGANISATEUR A SORTIES DU TUNNEL ═══
   *
   * ⚠️ APRÈS LES PAGES DE CONTENU, ET DANS LES DEUX LISTES DE LA MÊME FAÇON : le menu
   * et le gestionnaire doivent se lire dans le même ordre, sinon on règle la troisième
   * ligne et c'est la sixième entrée qui bouge.
   *
   * ⚠️ ET LE PARCOURS EST NÉCESSAIRE — lui seul sait combien d'étapes « informations »
   * existent. Voir `pages/stepPages.ts`.
   */
  const steps = stepPages(orderedEntries(value(ORDER_KEY)));

  /**
   * ═══ TOUTES LES PAGES, DANS L'ORDRE QUE L'ORGANISATEUR A RANGÉ ═══
   *
   * ⚠️ UNE SEULE LISTE ORDONNÉE, LUE PAR TOUT LE MONDE — le menu, la barre du bas, les
   * tuiles, le gestionnaire et le sélecteur d'écran. Ordonner chacun de son côté, c'est
   * régler la troisième ligne du gestionnaire et voir bouger la sixième entrée du menu.
   *
   * ⚠️ EXEMPLAIRES AJOUTÉS COMPRIS, SECTIONS COMME PAGES FORMULAIRE : la
   * configuration seule sait combien il y en a, comme elle seule sait combien
   * d'étapes « informations » le parcours sert. Oubliés ici, une page ajoutée
   * n'aurait ni ligne au gestionnaire, ni entrée au sélecteur : configurable nulle
   * part.
   */
  const readerSignedIn = useReaderSignedIn();
  const ordered = orderedPages(value(PAGES_ORDER_KEY), [
    ...withAdded(MANAGED_PAGES, value),
    ...steps,
  ]);
  const content = ordered.filter((p) => !isFixed(p.key));

  /**
   * ═══ CE QU'UN VISITEUR NON CONNECTÉ VOIT DANS LE MENU ET SUR L'ACCUEIL ═══
   *
   * ⚠️ UNE ENTRÉE QUI RENVOIE À LA CONNEXION N'EST PAS UNE ENTRÉE. Les pages fermées
   * disparaissent du menu et des tuiles tant que personne n'est connecté : les
   * laisser aurait offert huit portes dont sept se referment sur un formulaire de
   * connexion, sans dire laquelle était ouverte. La garde, elle, reste au châssis
   * (`Shell`) : c'est elle qui décide, ceci ne fait que ne pas mentir.
   *
   * ⚠️ SAUF EN MODE ORGANISATEUR, qui doit voir son site entier pour le régler — y
   * compris les pages qu'il n'a pas ouvertes. À moins qu'il n'ait demandé l'aperçu
   * « visiteur non connecté » de la barre : c'est tout l'objet de cet aperçu.
   */
  /**
   * ⚠️ UNE PAGE FERMÉE N'A NI ENTRÉE NI TUILE, coches comprises : elles mèneraient à la
   * page introuvable. Mode organisateur compris : le menu montre ce que verront les
   * participants, et l'organisateur rejoint la page par le sélecteur d'écran.
   */
  const shown = (p: SitePage) =>
    !isClosed(p, value) && (readerSignedIn || value(publicKey(p)) === true);

  return {
    name,
    subtitle,
    switchedOn,
    /** Toutes les pages du gestionnaire, dans l'ordre rangé. */
    ordered,
    /**
     * Les deux entrées fixes encore allumées.
     *
     * ⚠️ L'INSCRIPTION N'EST PLUS UNE EXCEPTION. Elle l'a été : offerte en dur à qui
     * n'est pas connecté, au motif que c'est LA porte d'un site public. Le motif
     * reste vrai, mais le choix appartient à l'organisateur, qui a maintenant son
     * interrupteur — comme pour l'accueil et les sections.
     */
    fixes: FIXED_PAGES.filter((p) => switchedOn(p, navKey(p)) && shown(p)),
    /** Dans le menu (haut en bureau, bas en mobile). */
    menu: content.filter(
      (p) =>
        (p.nameKey ? value(navKey(p)) === true : switchedOn(p, navKey(p))) &&
        shown(p),
    ),
    /** En tuile sur l'accueil — l'inscription comprise, si elle est demandée. */
    /**
     * ⚠️ LA TUILE D'INSCRIPTION EST ÉTEINTE PAR DÉFAUT, à l'inverse de son entrée de
     * menu : l'accueil porte déjà son bouton d'appel. Deux fois le même geste sur le
     * même écran, ce n'est pas une insistance, c'est une hésitation.
     *
     * Elle garde sa place en tête, comme dans le menu : c'est la même liste rangée.
     */
    tiles: ordered.filter(
      (p) =>
        (isFixed(p.key)
          ? FIXED_PAGES.find((f) => f.key === p.key)?.tile === true &&
            value(tileKey(p)) === true
          : p.nameKey
            ? value(tileKey(p)) === true
            : switchedOn(p, tileKey(p))) &&
        shown(p),
    ),
    /** Toutes les pages que les étapes PEUVENT servir — le gestionnaire les propose. */
    steps,
  };
}

/** Aller à une page, en gardant le contexte de l'événement dans l'adresse. */
function useGo() {
  const navigate = useNavigate();
  const event = useEvent();
  return (path: string, state?: unknown) =>
    navigate(withContext(path, event?.token), state ? { state } : undefined);
}

/**
 * ═══ OÙ MÈNE « S'INSCRIRE » ═══
 *
 * Trois chemins vers un seul endroit — l'entrée du menu, la tuile, le bouton de
 * l'accueil —, donc une seule fonction. La destination est la PREMIÈRE ÉTAPE SERVIE,
 * jamais une étape nommée en dur : avec l'inscription automatique, ou l'étape retirée
 * du parcours, un chemin écrit à la main mène à un écran que le participant ne doit
 * pas voir. `firstStep` lit le fil calculé.
 *
 * ⚠️ ET PAR LA CONNEXION QUAND PERSONNE N'EST CONNECTÉ. Le tunnel exige une session :
 * depuis qu'un refus renvoie à l'accueil public et non plus au formulaire de connexion,
 * la tuile « Inscription » d'un site public RENVOYAIT À L'ACCUEIL D'OÙ ON VENAIT DE
 * CLIQUER. Une porte qui ramène devant la porte. On passe donc par la connexion, en lui
 * disant où l'on allait : une fois entré, le parcours s'ouvre là où il devait.
 */
export function useEntry(): { path: string; state?: { from: string } } | null {
  const { value } = useSettings();
  const event = useEvent();
  const first = firstStep(value, useOffer());
  const reader = useReaderSignedIn();
  if (!first) return null;
  return reader
    ? { path: first.path }
    : {
        path: "/login",
        /* Préfixée du contexte : c'est l'adresse que `Login` rejouera telle quelle. */
        state: { from: withContext(first.path, event?.token) },
      };
}

/**
 * « Suis-je sur cette page ? » — en résolvant le CONTEXTE, pas en comparant des
 * bouts d'adresse.
 *
 * ⚠️ L'accueil s'écrit `/` en interne mais `/mon-evenement` dans la barre
 * d'adresse : une comparaison sur `/` ou un `endsWith("/")` ne le reconnaissait
 * jamais, et l'onglet Accueil n'était marqué courant sur AUCUNE page.
 *
 * ⚠️ ON RETIRE LE CONTEXTE, ON NE LE RAJOUTE PAS. La comparaison se faisait dans
 * l'autre sens — fabriquer l'adresse et l'égaler à celle du navigateur — et ce sens-là
 * casse dès que l'alias porte un accent : l'adresse est ENCODÉE
 * (`/sportachat%C3%A9t%C3%A92025/…`), ce que `withContext` ne produit jamais. Aucune
 * entrée de menu n'était plus marquée comme courante. `stripContext` sait comparer les
 * deux formes ; c'est donc lui qui tranche, ici comme dans le châssis.
 */
function useHere() {
  const { pathname } = useLocation();
  const event = useEvent();
  return (path: string) => stripContext(pathname, event?.token) === path;
}

/**
 * LE MENU DU HAUT — Accueil et Inscription d'abord, comme dans le prototype, puis
 * ce que l'organisateur a activé.
 *
 * ═══ CE QUI NE TIENT PAS PASSE DANS « PLUS » (bug #202) ═══
 *
 * ⚠️ LE MENU DÉFILAIT, SOUS UNE BARRE DE DÉFILEMENT MASQUÉE. Dix pages en 1280 px :
 * « Replay » rogné au bord, rien pour dire qu'il y avait une suite, rien que la
 * souris puisse saisir pour l'atteindre, et le focus d'une entrée lointaine faisait
 * défiler le menu jusqu'à couper « Accueil » en « ueil ». On MESURE donc ce qui
 * tient, et le reste se range derrière « Plus », comme la barre du bas le fait déjà
 * sur mobile. Une entrée est entière dans la barre, ou dans la liste : jamais à
 * moitié.
 */
type NavEntry = {
  key: string;
  label: string;
  current: boolean;
  go: () => void;
};

export function SiteNav() {
  const t = useT();
  const { menu, name, fixes } = useSitePages();
  const go = useGo();
  const { pathname } = useLocation();
  const ici = useHere();

  /**
   * ⚠️ L'INSCRIPTION MÈNE À LA PREMIÈRE ÉTAPE SERVIE, pas à une étape nommée en dur,
   * et par la connexion quand personne n'est connecté. Voir `useEntry`.
   */
  const entry = useEntry();

  /* ⚠️ ACCUEIL ET INSCRIPTION OUVRENT LE MENU, comme dans le prototype, mais ils
     s'éteignent, à la demande du client, dans le même gestionnaire que les autres.
     Leur nom aussi se règle et se traduit : c'est le mot de l'événement, pas le
     nôtre. */
  const entries: NavEntry[] = [
    ...fixes.flatMap((page): NavEntry[] => {
      if (page.key === "accueil")
        return [
          {
            key: page.key,
            label: name(page),
            current: ici("/"),
            go: () => go("/"),
          },
        ];
      return entry
        ? [
            {
              key: page.key,
              label: name(page),
              current: pathname.includes("/registration"),
              go: () => go(entry.path, entry.state),
            },
          ]
        : [];
    }),
    ...menu.map((page) => ({
      key: page.key,
      label: name(page),
      current: ici(pagePath(page)),
      go: () => go(pagePath(page)),
    })),
  ];

  const navRef = useRef<HTMLElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const shown = useFit(
    navRef,
    entries.map((e) => `${e.key}:${e.label}`).join("|"),
  );
  const overflows = entries.slice(shown);

  const [open, setOpen] = useState(false);
  const listId = useId();

  /* Échap et un clic dehors ferment, et le focus revient au bouton : sans quoi il
     repart au début du document. `mousedown`, comme le menu du compte : un clic
     enfoncé dehors et relâché dedans ne doit pas rouvrir ce qu'il vient de fermer. */
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  /* Une page choisie ailleurs (tuile, bouton) referme aussi la liste. */
  useEffect(() => setOpen(false), [pathname]);

  return (
    <nav
      className="nav"
      ref={navRef}
      aria-label={t("MAIN_NAVIGATION", "Navigation principale")}
      data-cfg="siteNav"
    >
      {entries.map((e, i) => (
        <button
          key={e.key}
          type="button"
          data-entry=""
          hidden={i >= shown}
          aria-current={e.current ? "page" : undefined}
          onClick={e.go}
        >
          {e.label}
        </button>
      ))}
      {/* ⚠️ RENDU PENDANT LA MESURE AUSSI (`shown` infini) : c'est là que sa largeur
          se prend, avant qu'on sache s'il en faudra un. */}
      <div
        className="nav__more"
        ref={moreRef}
        hidden={shown !== Infinity && overflows.length === 0}
      >
        <button
          type="button"
          ref={trigger}
          aria-expanded={open}
          aria-controls={listId}
          /* La page où l'on est peut être rangée dedans : « Plus » la désigne alors,
             sans quoi aucune entrée du menu ne dirait où l'on se trouve. */
          data-current={overflows.some((e) => e.current) || undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {t("MORE", "Plus")}
          <Icon name="chevron" />
        </button>
        <div className="nav__menu" id={listId} hidden={!open}>
          {overflows.map((e) => (
            <button
              key={e.key}
              type="button"
              aria-current={e.current ? "page" : undefined}
              onClick={() => {
                setOpen(false);
                e.go();
              }}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

/**
 * COMBIEN D'ENTRÉES TIENNENT DANS LE MENU, et `Infinity` le temps de les mesurer.
 *
 * ⚠️ UNE ENTRÉE RANGÉE N'A PLUS DE LARGEUR (elle est `hidden`) : on garde donc celle
 * qu'elle avait, prise quand tout était affiché. Les mots changent (langue, nom
 * réglé), une police arrive : tout se réaffiche le temps d'une mesure. C'est fait
 * avant que l'écran ne se peigne (`useLayoutEffect`) : personne ne voit le menu
 * déborder pendant ce temps.
 *
 * ⚠️ ET RIEN SOUS 720 PX : le menu y est `display: none` (c'est la barre du bas qui
 * navigue), ses entrées n'ont aucune largeur, et la mesure les rangerait toutes.
 */
function useFit(
  navRef: React.RefObject<HTMLElement | null>,
  signature: string,
): number {
  const [shown, setShown] = useState(Infinity);
  const widths = useRef(new Map<Element, number>());

  const fit = useCallback(() => {
    const nav = navRef.current;
    if (!nav || !nav.clientWidth) return;
    const items = [
      ...nav.querySelectorAll<HTMLElement>(":scope > [data-entry]"),
    ];
    const more = nav.querySelector<HTMLElement>(":scope > .nav__more");
    for (const el of [...items, more]) {
      if (el && !el.hidden)
        widths.current.set(el, el.getBoundingClientRect().width);
    }
    const sizes = items.map((el) => widths.current.get(el));
    const plus = more ? widths.current.get(more) : undefined;
    // Une largeur manque : la mesure complète est en route.
    if (plus === undefined || sizes.some((w) => w === undefined)) return;

    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
    const room = nav.clientWidth;
    const all = (sizes as number[]).reduce(
      (sum, w, i) => sum + w + (i ? gap : 0),
      0,
    );
    if (all <= room) return setShown(sizes.length);
    let used = plus;
    let n = 0;
    while (n < sizes.length && used + gap + sizes[n]! <= room) {
      used += gap + sizes[n]!;
      n++;
    }
    setShown(n);
  }, [navRef]);

  const remeasure = useCallback(() => {
    widths.current.clear();
    setShown(Infinity);
  }, []);

  // Les mots ont changé : tout se mesure à nouveau.
  useLayoutEffect(remeasure, [signature, remeasure]);
  useLayoutEffect(fit, [shown, signature, fit]);

  useEffect(() => {
    const nav = navRef.current;
    // jsdom n'en a pas : les tests qui montent le châssis gardent le menu entier.
    if (!nav || typeof ResizeObserver === "undefined") return;
    /* La place change avec la fenêtre ; la largeur d'une entrée affichée, avec le CSS
       de l'organisateur. Une entrée rangée garde sa dernière largeur connue. */
    const observer = new ResizeObserver(fit);
    observer.observe(nav);
    for (const el of nav.querySelectorAll(":scope > [data-entry]"))
      observer.observe(el);
    document.fonts?.addEventListener("loadingdone", remeasure);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener("loadingdone", remeasure);
    };
  }, [navRef, signature, fit, remeasure]);

  return shown;
}

/**
 * LA BARRE DU BAS, sur mobile — et son DÉBORD.
 *
 * ⚠️ QUATRE AU PLUS, puis « Plus », exactement comme le prototype : au-delà, des
 * onglets de trente pixels avec un libellé coupé au milieu, sur lesquels personne ne
 * vise juste. À cinq pages et plus, on n'en montre que trois et le reste passe dans
 * la feuille.
 */
export function SiteTabbar() {
  const t = useT();
  const { menu, name, fixes } = useSitePages();
  const go = useGo();
  const [plus, setPlus] = useState(false);
  const { pathname } = useLocation();
  const ici = useHere();
  const here = (path: string) => (ici(path) ? "page" : undefined);

  /**
   * ⚠️ L'INSCRIPTION EST AUSSI DANS LA BARRE, comme dans le menu du haut. Sous 390 px ce
   * menu-là n'existe pas : la barre est la seule navigation du téléphone, et elle ne
   * portait que l'accueil et les pages de contenu. Accueil vide, bouton d'appel et tuile
   * éteints, plus rien ne menait à l'inscription (bug #255). Même destination que le
   * menu — voir `useEntry` —, et absente quand le parcours n'a plus d'étape servie.
   */
  const entry = useEntry();
  const home = fixes.find((p) => p.key === "accueil");
  const inscription = entry
    ? fixes.find((p) => p.key === "inscription")
    : undefined;

  // Le compte porte sur l'inscription et les PAGES, Accueil en plus — c'est le
  // découpage du prototype : quatre onglets au plus, sinon trois et un « Plus ».
  const room = inscription ? 3 : 4;
  const visibles = menu.slice(0, menu.length > room ? room - 1 : room);
  const overflows = menu.slice(visibles.length);

  const plusButton = useRef<HTMLButtonElement | null>(null);
  const sheet = useRef<HTMLDivElement | null>(null);
  const sheetId = useId();

  /* Refermée sans choisir de page, la feuille rend le focus à « Plus » : le choix
     qui l'avait était masqué avec elle, et le focus repartait au début du document. */
  const closeSheet = useCallback(() => {
    setPlus(false);
    plusButton.current?.focus();
  }, []);

  /**
   * ⚠️ LA FEUILLE EST UN DIALOGUE MODAL, ET LE CLAVIER Y RESTE (bug #409). Ouverte par
   * Entrée sur « Plus », elle ne se refermait plus : ni Échap, ni second appui, et Tab
   * continuait sur la page qu'elle recouvre. À l'ouverture le focus va au premier
   * choix, Tab et Maj+Tab bouclent sur ses choix, Échap la referme comme le menu du
   * haut.
   */
  useEffect(() => {
    if (!plus) return;
    const choices = () => [
      ...(sheet.current?.querySelectorAll<HTMLButtonElement>("button") ?? []),
    ];
    choices()[0]?.focus();
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeSheet();
        return;
      }
      if (e.key !== "Tab") return;
      const list = choices();
      const first = list[0];
      const last = list[list.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      const inside = sheet.current?.contains(active) ?? false;
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keys);
    return () => document.removeEventListener("keydown", keys);
  }, [plus, closeSheet]);

  return (
    <>
      <nav
        className="tabbar"
        aria-label={t("NAVIGATION", "Navigation")}
        /**
         * ⚠️ RIEN À NAVIGUER, PAS DE BARRE. Le prototype la montre toujours parce que
         * sa démonstration a des pages activées ; sur un événement qui n'en a aucune,
         * une bande de 44 px offrant le seul écran où l'on est déjà n'est pas de la
         * navigation, c'est du décor — et sur un téléphone c'est 44 px pris au
         * formulaire. L'inscription, elle, compte : c'est une autre page que celle-ci.
         */
        hidden={menu.length === 0 && !inscription}
        data-cfg="siteNav"
      >
        {/* Accueil ouvre la barre aussi — c'est le retour, et sur un téléphone le nom
            de l'événement en haut est une bien petite cible. Éteint dans le
            gestionnaire, il disparaît d'ici comme du menu. */}
        {home && (
          <button
            type="button"
            aria-current={here("/")}
            onClick={() => go("/")}
          >
            <Icon name={home.icon} />
            <span>{name(home)}</span>
          </button>
        )}
        {inscription && entry && (
          <button
            type="button"
            aria-current={
              pathname.includes("/registration") ? "page" : undefined
            }
            onClick={() => go(entry.path, entry.state)}
          >
            <Icon name={inscription.icon} />
            <span>{name(inscription)}</span>
          </button>
        )}
        {visibles.map((page) => (
          <button
            key={page.key}
            type="button"
            aria-current={here(pagePath(page))}
            onClick={() => go(pagePath(page))}
          >
            <Icon name={page.icon} />
            <span>{name(page)}</span>
          </button>
        ))}
        {overflows.length > 0 && (
          <button
            ref={plusButton}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={plus}
            aria-controls={sheetId}
            onClick={() => setPlus((o) => !o)}
          >
            <Icon name="plus" />
            <span>{t("MORE", "Plus")}</span>
          </button>
        )}
      </nav>

      {/* La feuille du débord. `hidden` plutôt qu'un rendu conditionnel : c'est
          ainsi que le châssis l'allume (`.plus:not([hidden])`). */}
      <div className="plus" hidden={!plus}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div className="plus__bg" onClick={closeSheet} />
        <div
          ref={sheet}
          id={sheetId}
          className="plus__c"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${sheetId}-t`}
        >
          <p className="plus__t" id={`${sheetId}-t`}>
            {t("REST_OF_THE_SITE", "Le reste du site")}
          </p>
          <div className="plus__l">
            {overflows.map((page) => (
              <button
                key={page.key}
                type="button"
                onClick={() => {
                  setPlus(false);
                  go(pagePath(page));
                }}
              >
                <Icon name={page.icon} />
                <span>{name(page)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * LES TUILES DE L'ACCUEIL.
 *
 * Le sous-titre vient du registre, pas d'un réglage : c'est le choix du prototype,
 * et il tient — il dit ce que la page CONTIENT, ce qui ne dépend pas de l'événement.
 */
export function SiteTiles() {
  const { tiles, name, subtitle } = useSitePages();
  const go = useGo();
  const entry = useEntry();

  /**
   * ⚠️ LA TUILE D'INSCRIPTION NE MÈNE PAS À `/inscription` : cette adresse n'existe
   * pas, l'inscription est le TUNNEL. Sa destination se calcule une fois pour les
   * trois chemins qui y mènent — voir `useEntry`.
   */
  const toward = (page: SitePage) =>
    page.key === "inscription" ? entry?.path : pagePath(page);

  if (tiles.length === 0) return null;

  return (
    <div className="tiles" data-cfg="homeTiles" data-gk="home-tiles">
      {tiles.map((page) => (
        <button
          key={page.key}
          type="button"
          className="tile"
          data-tile={page.key}
          // Une tuile dont la destination n'existe pas ne se dessine pas : c'est le
          // cas de l'inscription quand le parcours n'a plus une seule étape servie.
          hidden={!toward(page)}
          onClick={() => {
            const target = toward(page);
            if (target) go(target, page.key === "inscription" ? entry?.state : undefined);
          }}
        >
          <span className="tile__i" aria-hidden="true">
            <Icon name={page.icon} />
          </span>
          <b>{name(page)}</b>
          {/* Une section sans description assumée : pas de <small> vide
              qui laisserait sa hauteur de ligne sous le nom. */}
          {subtitle(page).trim() !== "" && <small>{subtitle(page)}</small>}
        </button>
      ))}
    </div>
  );
}
